import { ReadFile } from '@paragon/client-tools';
import {
  BearingEnvelope,
  CeilingPlane,
  CeilingTray,
  ComponentDesign,
  ComponentDesignTimestamps,
  CreateTrussError,
  DxfLayout,
  EstimateMetrics,
  FeaturePatternTrussContainer,
  JackGirderConnection,
  NewTrussGroup,
  Project,
  ProjectChange,
  ProjectLayout,
  ProjectSummary,
  ProjectSynchronizationPayload,
  RoofContainer,
  RoofPlane,
  Segment2D,
  SnappingUnderlay,
  TrussEnvelope,
  TrussGroup,
  WorkLine,
} from '@paragon/contract-library';

import { DesignService } from '../connections';
import { DownloadableFileResponse } from '../responses';

export function fetchAll(): Promise<Project[]> {
  return DesignService.get('projects');
}

export function fetch(projectGuid: string): Promise<Project> {
  return DesignService.get(`projects/${projectGuid}`);
}

export function fetchMetrics(projectGuid: string) {
  return DesignService.get<EstimateMetrics>(`projects/${projectGuid}/metrics`);
}

export function fetchSummaries(): Promise<ProjectSummary[]> {
  return DesignService.get('projectSummaries');
}

export function fetchProjectsWhichHaveProductionGroups() {
  return DesignService.get<readonly Project[]>('projects/whichHaveProductionGroups');
}

export function exportProject(projectGuid: string, layoutOnly = false) {
  const query = new URLSearchParams({ layoutOnly: `${layoutOnly}` }).toString();
  return DesignService.fetchFile(`projects/${projectGuid}/zip?${query}`);
}

export function create(project: Project): Promise<Project> {
  return DesignService.post('projects', { data: project });
}

export function duplicate(projectGuid: string): Promise<Project> {
  return DesignService.post<Project>(`projects/${projectGuid}/duplicate`);
}

export function applyChanges(changes: ProjectChange): Promise<Project> {
  return DesignService.post(`projects/${changes.projectGuid}/changes`, { data: changes });
}

export function archive(guid: string) {
  return DesignService.post(`projects/${guid}/archive`);
}

export function importPpf(projectGuid: string, base64Ppf: ReadFile, layoutOnly = false) {
  const query = new URLSearchParams({ layoutOnly: `${layoutOnly}` }).toString();
  return DesignService.post(`projects/${projectGuid}/importPpf?${query}`, { data: base64Ppf });
}

export function replaceRoofContainers(projectGuid: string, roofContainers: Record<string, RoofContainer>) {
  return DesignService.put(`projects/${projectGuid}/roofContainers`, { data: roofContainers });
}

export function upsertRoofContainer(projectGuid: string, roofContainer: RoofContainer) {
  return DesignService.put(`projects/${projectGuid}/roofContainers/${roofContainer.guid}`, { data: roofContainer });
}

export function deleteRoofContainer(projectGuid: string, roofContainerGuid: string) {
  return DesignService.delete(`projects/${projectGuid}/roofContainers/${roofContainerGuid}`);
}

export function deleteFeaturePatternContainers(projectGuid: string, featurePatternContainerGuids: readonly string[]) {
  return DesignService.delete(`projects/${projectGuid}/featurePatternContainers`, {
    data: featurePatternContainerGuids,
  });
}

export function getTrussEnvelopes(projectGuid: string) {
  return DesignService.get<Record<string, TrussEnvelope>>(`projects/${projectGuid}/trussEnvelopes`);
}

export function replaceTrussEnvelopes(projectGuid: string, trussEnvelopes: Record<string, TrussEnvelope>) {
  return DesignService.put<ProjectSynchronizationPayload>(`projects/${projectGuid}/trussEnvelopes`, {
    data: trussEnvelopes,
  });
}

export function upsertTrussEnvelopes(projectGuid: string, envelopes: readonly TrussEnvelope[]) {
  return DesignService.post<ProjectSynchronizationPayload>(`projects/${projectGuid}/upsertTrussEnvelopes`, {
    data: envelopes,
  });
}

export function deleteTrussEnvelopes(projectGuid: string, trussEnvelopeGuids: string[]) {
  return DesignService.delete<ProjectSynchronizationPayload>(`projects/${projectGuid}/trussEnvelopes`, {
    data: trussEnvelopeGuids,
  });
}

export function replaceBearingEnvelopes(projectGuid: string, bearingEnvelopes: Record<string, BearingEnvelope>) {
  return DesignService.put(`projects/${projectGuid}/bearingEnvelopes`, { data: bearingEnvelopes });
}

export function upsertBearingEnvelope(projectGuid: string, bearingEnvelope: BearingEnvelope) {
  return DesignService.put(`projects/${projectGuid}/bearingEnvelopes/${bearingEnvelope.guid}`, {
    data: bearingEnvelope,
  });
}

export function replaceRoofPlanes(projectGuid: string, roofPlanes: Record<string, RoofPlane>) {
  return DesignService.put(`projects/${projectGuid}/roofPlanes`, { data: roofPlanes });
}

export function replaceCeilingPlanes(projectGuid: string, ceilingPlanes: Record<string, CeilingPlane>) {
  return DesignService.put(`projects/${projectGuid}/ceilingPlanes`, { data: ceilingPlanes });
}

export function replaceCeilingTrays(projectGuid: string, ceilingTrays: Record<string, CeilingTray>) {
  return DesignService.put(`projects/${projectGuid}/ceilingTrays`, { data: ceilingTrays });
}

export function replaceFeaturePatternContainers(
  projectGuid: string,
  featurePatternContainers: Record<string, FeaturePatternTrussContainer>,
) {
  return DesignService.put(`projects/${projectGuid}/featurePatternContainers`, { data: featurePatternContainers });
}

export function replaceWorkLines(projectGuid: string, workLines: Record<string, WorkLine>) {
  return DesignService.put(`projects/${projectGuid}/workLines`, { data: workLines });
}

export function replaceSnappingUnderlays(projectGuid: string, snappingUnderlays: Record<string, SnappingUnderlay>) {
  return DesignService.put(`projects/${projectGuid}/snappingUnderlays`, { data: snappingUnderlays });
}

export function uploadLayoutFile(projectGuid: string, layoutFile: string) {
  return DesignService.post(`projects/${projectGuid}/uploadLayoutFile`, { data: layoutFile });
}

export function uploadLayoutDxfFile(projectGuid: string, file: ReadFile) {
  return DesignService.post<Segment2D[]>(`projects/${projectGuid}/uploadLayoutDxf`, { data: file });
}

export function extractDxfLayouts(projectGuid: string, file: ReadFile) {
  return DesignService.post<DxfLayout[]>(`projects/${projectGuid}/extractDxfLayouts`, { data: file });
}

export function getLayoutDxfFiles(
  projectGuid: string,
  automaticDimensionSegments: SegmentForLevel[],
  selectedLevelGuid?: string,
  nameOverride?: string,
): Promise<DownloadableFileResponse> {
  const params = new URLSearchParams();
  if (selectedLevelGuid) params.append('selectedLevelGuid', selectedLevelGuid);
  if (nameOverride) params.append('nameOverride', nameOverride);
  return DesignService.fetchFile(`projects/${projectGuid}/layoutDxf`, {
    data: automaticDimensionSegments,
    post: true,
    params,
  });
}

export function getLayout(projectGuid: string) {
  return DesignService.get<ProjectLayout>(`projects/${projectGuid}/layout`);
}

export function replaceLayout(layout: ProjectLayout) {
  return DesignService.put(`projects/${layout.projectGuid}/layout`, { data: layout });
}

interface RegenerateEnvelopeConnectionsResponse {
  readonly createdJackGirderConnections: readonly JackGirderConnection[];
  readonly updatedJackGirderConnections: readonly JackGirderConnection[];
  readonly deletedJackGirderConnectionGuids: readonly string[];
  readonly timestamps: readonly ComponentDesignTimestamps[];
}

export function regenerateEnvelopeConnections(projectGuid: string, envelopesToRegenerate: readonly TrussEnvelope[]) {
  return DesignService.post<RegenerateEnvelopeConnectionsResponse>(
    `projects/${projectGuid}/regenerateEnvelopeConnections`,
    { data: envelopesToRegenerate },
  );
}

export interface SegmentForLevel {
  levelGuid: string | null;
  segment: Segment2D;
}

export function getLayoutPdf(
  projectGuid: string,
  automaticDimensionSegments: SegmentForLevel[],
  selectedLevelGuid?: string,
  nameOverride?: string,
  levelName?: string,
): Promise<DownloadableFileResponse> {
  const params = new URLSearchParams();
  if (selectedLevelGuid) params.append('selectedLevelGuid', selectedLevelGuid);
  if (nameOverride) params.append('nameOverride', nameOverride);
  if (levelName) params.append('levelName', levelName);
  return DesignService.fetchFile(`projects/${projectGuid}/layoutPdf`, {
    data: automaticDimensionSegments,
    post: true,
    params,
  });
}

export function downloadLayoutIfc(
  projectGuid: string,
  name: string,
  selectedLevelGuid?: string,
): Promise<DownloadableFileResponse> {
  const params = new URLSearchParams({ projectGuid, name });
  if (selectedLevelGuid) params.append('selectedLevelGuid', selectedLevelGuid);
  return DesignService.fetchFile('projects/layoutIfc', {
    params,
  });
}

interface LayoutGenerationResponse {
  readonly createdTrussEnvelopes: readonly TrussEnvelope[];
  readonly synchronizationPayload: ProjectSynchronizationPayload;
}

export function generateTrussEnvelopes(projectGuid: string) {
  return DesignService.post<LayoutGenerationResponse>(`projects/${projectGuid}/generateEnvelopes`);
}

export function optimizeFeaturePatternContainerLayout(projectGuid: string, featurePatternContainerGuid: string) {
  return DesignService.post<LayoutGenerationResponse>(`projects/${projectGuid}/optimizeFeaturePatternContainerLayout`, {
    data: featurePatternContainerGuid,
  });
}

interface CreateTrussesResponse {
  readonly updatedTrussEnvelopes: readonly TrussEnvelope[];
  readonly createdComponentDesigns: readonly ComponentDesign[];
  readonly errors: { readonly [trussEnvelopeGuid: string]: CreateTrussError };
  readonly synchronizationPayload: ProjectSynchronizationPayload;
  readonly jackGirderConnections: readonly JackGirderConnection[];
  readonly trussEnvelopeGuidsToWarnIntersectingPlanes: readonly string[];
}

export function createTrusses(
  projectGuid: string,
  trussEnvelopes: readonly TrussEnvelope[],
  nameSuggestions: { readonly [trussEnvelopeGuid: string]: string } | null,
) {
  return DesignService.post<CreateTrussesResponse>(`projects/${projectGuid}/createTrusses`, {
    data: { trussEnvelopes, nameSuggestions },
  });
}

interface CreateTrussOkResponse {
  readonly type: 'Ok';
  readonly updatedTrussEnvelope: TrussEnvelope;
  readonly createdComponentDesign: ComponentDesign | null;
  readonly synchronizationPayload: ProjectSynchronizationPayload;
  readonly jackGirderConnections: readonly JackGirderConnection[];
  readonly warnIntersectingPlanes: boolean;
}

interface CreateTrussErrResponse {
  readonly type: 'Err';
  readonly error: CreateTrussError;
}

export function createTruss(projectGuid: string, trussEnvelope: TrussEnvelope, matchExisting: boolean) {
  return DesignService.post<CreateTrussOkResponse | CreateTrussErrResponse>(`projects/${projectGuid}/createTruss`, {
    params: new URLSearchParams({ matchExisting: matchExisting.toString() }),
    data: trussEnvelope,
  });
}

interface AutoCutResponse {
  readonly roofPlanes: readonly RoofPlane[];
}

export function autoCutPlanes(projectGuid: string, roofPlaneGuids: readonly string[]) {
  return DesignService.post<AutoCutResponse>(`projects/${projectGuid}/autoCutPlanes`, {
    data: { roofPlaneGuids },
  });
}

export function getTrussGroups(projectGuid: string) {
  return DesignService.get<TrussGroup[]>(`public/projects/${projectGuid}/trussGroups`);
}

export function createTrussGroup(projectGuid: string, newTrussGroup: NewTrussGroup) {
  return DesignService.post<TrussGroup>(`public/projects/${projectGuid}/trussGroups`, {
    data: newTrussGroup,
  });
}

export function updateTrussGroup(projectGuid: string, trussGroup: TrussGroup) {
  return DesignService.put<void>(`public/projects/${projectGuid}/trussGroups`, {
    data: trussGroup,
  });
}

export function deleteTrussGroups(projectGuid: string, trussGroupGuids: readonly string[]) {
  return DesignService.delete<void>(`public/projects/${projectGuid}/trussGroups`, {
    data: trussGroupGuids,
  });
}
